import React, { useEffect, useState } from 'react'
import { useGlobalState } from '@/state'
import { Link } from 'react-router-dom'
import { formatInTimezone } from '@/utils/date'
import { CalendarIcon, ArrowRightIcon } from '@heroicons/react/solid'

import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import EventRow from '@/shared/EventRow'
import JobsPanel from '@/shared/JobsPanel'

const Dashboard = () => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [dashboardContainer] = useGlobalState('dashboardContainer')
  const [ enrolledCohorts, setEnrolledCohorts ] = useState([])
  const [ pastCohorts, setPastCohorts ] = useState([])
  const [ loading, setLoading ] = useState(false)

  const urlParams = new window.URLSearchParams(window.location.search)
  const promo = urlParams.get('promo')

  const { events, classSessions, attendingEventIds } = dashboardContainer

  useEffect(() => {
    setLoading(true)
    getRequest(`/api/v1/users/dashboard`, {}, (err, jsonData) => {
      if (err) { return }
      setEnrolledCohorts(jsonData.enrolledCohorts)
      setPastCohorts(jsonData.pastCohorts)
      setLoading(false)
    })
  }, [])

  const hour = new Date().getHours()
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
  const firstName = currentUser?.firstName || currentUser?.displayName?.split(' ')[0] || 'there'

  const contextLine = () => {
    if (classSessions?.length > 0) {
      const count = classSessions.length
      return `You have ${count} class ${count === 1 ? 'session' : 'sessions'} coming up.`
    }
    if (events?.length > 0) {
      const count = events.length
      return `${count} ${count === 1 ? 'event' : 'events'} in the next four weeks.`
    }
    return `Nothing on the calendar this week. Good time to browse the library or explore a new course.`
  }

  return <div className='py-8'>
    { loading && <Loading /> }

    {/* Greeting band with branded gradient */}
    <div className='mb-8 rounded-2xl bg-gradient-to-br from-clcpurple via-clcpink to-clcblue-soft p-8 sm:p-10 shadow-xl shadow-clcpurple/20'>
      <h1 className='text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight drop-shadow-sm'>
        {timeGreeting}, {firstName}.
      </h1>
      <p className='mt-2 text-lg text-white/90'>
        {contextLine()}
      </p>
    </div>

    <JobsPanel />

    { promo && <div className="mb-8">
      <div className="bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 p-8 text-center">
        <span className="inline-block px-3 py-1 text-xs font-bold tracking-wider uppercase text-white bg-green-600 rounded-full">
          Confirmation
        </span>
        <p className="mt-4 text-2xl font-bold text-slate-900">Promo code applied</p>
        <p className="mt-2 text-base text-slate-600">You are now a member of Closing Credits from your Casting Call Club Business Plan.</p>
      </div>
    </div> }

    { enrolledCohorts.length > 0 && <section className='mt-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
        Courses you're currently taking
      </h2>

      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        { enrolledCohorts.map((cohort) => (
          <Link to={`/c/${cohort.courseSlug}`} key={`dasco${cohort.id}`} className="group bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-md shadow-slate-900/[0.05] hover:shadow-xl hover:shadow-clcpurple/20 hover:border-clcpurple/40 hover:-translate-y-0.5 hover:no-underline transition-all duration-300">
            <div className="aspect-w-16 aspect-h-9 overflow-hidden">
              <img className="object-cover w-full transition-transform duration-500 group-hover:scale-105" src={cohort.imageUrl} alt="" />
            </div>
            <div className="p-5">
              <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 leading-snug group-hover:text-clcpurple dark:group-hover:text-clcpurple-light transition-colors">{cohort.name}</h3>
              <div className="mt-3 flex items-center text-sm text-slate-500 dark:text-slate-400">
                <CalendarIcon className='w-4 h-4 mr-1.5 shrink-0' />
                <span>{formatInTimezone(cohort.startsAtUtc, currentUser.timezone, 'MMM d')} – {formatInTimezone(cohort.endsAtUtc, currentUser.timezone, 'MMM d, Y')}</span>
              </div>
              <div className="mt-4 inline-flex items-center text-sm font-bold text-clcblue dark:text-clcnavy-light group-hover:text-clcnavy-dark transition-colors">
                View course
                <ArrowRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section> }

    <section className='mt-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
        Upcoming class sessions
      </h2>

      <div className="mt-6">
        { classSessions.length === 0 ? (
          <div className="text-center py-10">
            <div className="mx-auto h-12 w-12 text-slate-300 dark:text-slate-600">
              <svg fill="none" stroke="currentColor" viewBox="0 0 48 48" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C12 4.56 13.79 3 16.253 3h15.494C34.21 3 36 4.56 36 6.253v13M12 6.253L8.224 9.44c-.707.707-.707 1.853 0 2.56L12 15.747m24-9.494L39.776 9.44c.707.707.707 1.853 0 2.56L36 15.747m-24 0v21.494C12 39.44 13.79 41 16.253 41h15.494C34.21 41 36 39.44 36 37.247V15.747M12 15.747l4-4h16l4 4" />
              </svg>
            </div>
            <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-slate-100">No class sessions this week</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
              Enroll in a course and your upcoming classes will show up here so you never miss one.
            </p>
            <a href="/courses"
              className="mt-5 inline-flex items-center justify-center px-6 py-3 text-sm font-bold rounded-lg text-white bg-clcblue shadow-md hover:bg-clcnavy-dark hover:shadow-lg transition-all"
            >
              Browse courses
              <ArrowRightIcon className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            { classSessions.map((event) => <EventRow key={`dashboardcs${event.id}`} event={event} user={currentUser} attendingEventIds={attendingEventIds} /> )}
          </div>
        )}
      </div>
    </section>

    <section className='mt-8 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-900/[0.07] p-6 sm:p-8'>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
          Events in the next four weeks
        </h2>
        <Link to='/events' className="text-sm font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark transition-colors">
          See all upcoming events →
        </Link>
      </div>

      <div className="mt-6">
        { events.length === 0 ? (
          <div className="text-center py-10">
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">No events in the next four weeks</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto">
              Time for a breather. In the meantime, you can <Link to='/chat' className="font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark">chit chat</Link> with other members or <Link to='/examples' className="font-bold text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark">read up</Link> on things you've been meaning to.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            { events.map((event) => <EventRow key={`dashboardev${event.id}`} event={event} user={currentUser} attendingEventIds={attendingEventIds} /> )}
          </div>
        )}
      </div>
    </section>
  </div>
}

export default Dashboard
